if (typeof ProductMainGallery === 'undefined') {
  class ProductMainGallery extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.pendingSlideIndex = null;
      this.onThumbClick = this.onThumbClick.bind(this);
    }

    connectedCallback() {
      this.thumbsRoot = this.querySelector('[data-gallery-thumbs]');
      if (this.thumbsRoot) {
        this.thumbsRoot.addEventListener('click', this.onThumbClick);
      }
      this.initSwiper();
    }

    disconnectedCallback() {
      if (this.thumbsRoot) {
        this.thumbsRoot.removeEventListener('click', this.onThumbClick);
      }
      this.destroySwiper();
    }

    get swiperEl() {
      return this.querySelector('[data-gallery-swiper]');
    }

    getSlideCount() {
      const el = this.swiperEl;
      return el ? el.querySelectorAll('.swiper-slide').length : 0;
    }

    getSwiperConfig() {
      const slideCount = this.getSlideCount();
      const isSingle = slideCount <= 1;
      const initialSlide = parseInt(this.swiperEl?.dataset.initialSlide || '0', 10);

      return {
        a11y: true,
        // Multi-image: peek carousel. Single image: full-width centered, no offsets.
        slidesPerView: isSingle ? 1 : 1.2,
        spaceBetween: isSingle ? 0 : 16,
        slidesOffsetBefore: isSingle ? 0 : 16,
        slidesOffsetAfter: isSingle ? 0 : 16,
        centeredSlides: isSingle,
        allowTouchMove: !isSingle,
        speed: 420,
        resistanceRatio: 0.7,
        initialSlide: Number.isFinite(initialSlide) ? initialSlide : 0,
        watchOverflow: true,
        grabCursor: slideCount > 1,
        observer: true,
        observeParents: true,
        breakpoints: {
          1025: {
            slidesPerView: 1,
            spaceBetween: 0,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            centeredSlides: false,
            allowTouchMove: false,
            grabCursor: false
          }
        },
        on: {
          init: (swiper) => {
            this.syncThumbs(swiper.activeIndex);
            if (this.pendingSlideIndex != null) {
              const index = this.pendingSlideIndex;
              this.pendingSlideIndex = null;
              swiper.slideTo(index, 0);
            }
          },
          slideChange: (swiper) => this.syncThumbs(swiper.activeIndex)
        }
      };
    }

    initSwiper() {
      const el = this.swiperEl;
      if (!el || this.swiper || this.getSlideCount() < 1) return;

      const start = () => {
        if (!this.isConnected || this.swiper || typeof window.Swiper === 'undefined') return;
        try {
          this.swiper = new window.Swiper(el, this.getSwiperConfig());
        } catch (error) {
          console.error('Failed to initialize product gallery Swiper:', error);
        }
      };

      if (typeof window.whenReady === 'function') {
        window.whenReady('Swiper', start);
      } else {
        start();
      }
    }

    destroySwiper() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    onThumbClick(event) {
      const thumb = event.target.closest('[data-slide-index]');
      if (!thumb || !this.contains(thumb)) return;

      const index = parseInt(thumb.getAttribute('data-slide-index'), 10);
      if (Number.isNaN(index)) return;
      this.slideTo(index);
    }

    syncThumbs(index) {
      const thumbs = this.querySelectorAll('[data-gallery-thumbs] [data-slide-index]');
      thumbs.forEach((thumb) => {
        const isActive = parseInt(thumb.getAttribute('data-slide-index'), 10) === index;
        thumb.classList.toggle('active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const active = this.querySelector('[data-gallery-thumbs] [data-slide-index].active');
      const thumbsRoot = this.thumbsRoot;
      if (!active || !thumbsRoot || typeof active.scrollIntoView !== 'function') return;

      const rootRect = thumbsRoot.getBoundingClientRect();
      const thumbRect = active.getBoundingClientRect();
      const clipped =
        thumbRect.left < rootRect.left - 1 || thumbRect.right > rootRect.right + 1;

      if (clipped) {
        active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    slideTo(index) {
      if (!Number.isFinite(index) || index < 0) return;

      if (this.swiper) {
        this.swiper.slideTo(index);
        return;
      }

      this.pendingSlideIndex = index;
      this.syncThumbs(index);
    }

    resolveImageId(image) {
      if (image == null) return null;
      if (typeof image === 'number' || typeof image === 'string') return String(image);
      if (image.id != null) return String(image.id);
      if (image.image_id != null) return String(image.image_id);
      return null;
    }

    findSlideIndex(image) {
      const imageId = this.resolveImageId(image);
      let slide = imageId
        ? this.querySelector(`.product-main__gallery-slide[data-image-id="${imageId}"]`)
        : null;

      if (!slide && image && image.src) {
        const needle = String(image.src).split('?')[0].replace(/^https?:/, '');
        this.querySelectorAll('.product-main__gallery-slide img').forEach((img) => {
          if (slide) return;
          const src = (img.getAttribute('src') || '').split('?')[0].replace(/^https?:/, '');
          if (src && needle && (src.includes(needle) || needle.includes(src))) {
            slide = img.closest('.product-main__gallery-slide');
          }
        });
      }

      // Fallback: match against product JSON image list by id/src
      if (!slide && imageId && window.productMainProduct && Array.isArray(productMainProduct.images)) {
        const match = productMainProduct.images.find((img) => {
          if (img == null) return false;
          if (typeof img === 'string') return false;
          return String(img.id) === imageId;
        });
        if (match && match.position != null) {
          return Math.max(0, Number(match.position) - 1);
        }
      }

      if (!slide) return null;

      const index = parseInt(slide.getAttribute('data-slide-index'), 10);
      if (!Number.isNaN(index)) return index;
      return [...slide.parentElement.children].indexOf(slide);
    }

    slideToImage(image) {
      if (!image) return;
      const index = this.findSlideIndex(image);
      if (index == null) return;
      this.slideTo(index);
    }
  }

  customElements.define('product-main-gallery', ProductMainGallery);
  window.ProductMainGallery = ProductMainGallery;
}
